import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { ActivityCard } from "./ActivityCard";
import { formatDate, downloadPDF } from "../../../utils/exportUtils";
import {
  AssessmentIcon,
  CheckInIcon,
  IncidentIcon,
  KudosIcon,
  MediaIcon,
  MedsIcon,
  NotesIcon,
  ObservationIcon,
  QuranIcon,
  RecitationIcon,
  CustomIcon,
} from "../../../assets/svg/icons";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { useTranslation } from "react-i18next";
import MenuBookIcon from "@mui/icons-material/MenuBook";

const ActivityFeed = ({ studentId }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const observerRef = useRef(null);
  const printRef = useRef(null); // wraps everything that should appear in the PDF
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityType, setActivityType] = useState("all");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  // ─── Date range filter ───────────────────────────────────────────────────
  // Empty string = no bound on that side. Sent to backend as start_date/end_date.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState(null);

  const activityOptions = [
    { value: "all", label: "studentDetails.activityFeed.allActivities" },
    { value: "custom", label: "studentDetails.activityFeed.custom" },
    { value: "checkin", label: "studentDetails.activityFeed.checkin" },
    { value: "photo", label: "studentDetails.activityFeed.photo" },
    { value: "note", label: "studentDetails.activityFeed.note" },
    // { value: "meds", label: "studentDetails.activityFeed.meds" },
    { value: "quran", label: "studentDetails.activityFeed.quran" },
    { value: "observation", label: "studentDetails.activityFeed.observation" },
    { value: "incident", label: "studentDetails.activityFeed.incident" },
    { value: "kudos", label: "studentDetails.activityFeed.kudos" },
    { value: "quranassessment", label: "studentDetails.activityFeed.assessment" },
    { value: "recitation", label: "studentDetails.activityFeed.recitation" },
  ];

  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = API_URLS.getFeed(studentId, activityType, null, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const response = await axiosPrivate.get(url);
      setHasMore(response?.data?.has_more);
      setNextCursor(response?.data?.next_cursor);
      const data = response.data.activities;
      setActivities(data);
    } catch (error) {
      console.error("GET request error:", error);
      setError(t("studentDetails.activityFeed.error"));
    } finally {
      setLoading(false);
    }
  }, [activityType, axiosPrivate, studentId, startDate, endDate, t]);

  const loadMoreFeed = useCallback(async () => {
    if (loading || loadingMore || !hasMore || !nextCursor) return;
    try {
      setError(null);
      setLoadingMore(true);
      const url = API_URLS.getFeed(studentId, activityType, nextCursor, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const response = await axiosPrivate.get(url);
      setHasMore(response?.data?.has_more);
      setNextCursor(response?.data?.next_cursor);
      const data = response.data.activities;
      setActivities((prev) => [...prev, ...data]);
    } catch (error) {
      console.error("GET request error:", error);
      setError(t("studentDetails.activityFeed.error"));
    } finally {
      setLoadingMore(false);
    }
  }, [
    activityType,
    axiosPrivate,
    hasMore,
    loading,
    loadingMore,
    nextCursor,
    studentId,
    startDate,
    endDate,
  ]);

  // Effect to reset hasMore and nextCursor on changing the activity type or date range
  useEffect(() => {
    setHasMore(false);
    setNextCursor(null);
  }, [activityType, startDate, endDate]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const target = observerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreFeed();
        }
      },
      { root: null, threshold: 0 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMoreFeed]);

  // ─── Date validation: "from" can't be after "to" ────────────────────────
  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value && endDate && value > endDate) {
      setDateError(t("studentDetails.activityFeed.dateRangeError"));
    } else {
      setDateError(null);
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (startDate && value && startDate > value) {
      setDateError(t("studentDetails.activityFeed.dateRangeError"));
    } else {
      setDateError(null);
    }
  };

  // ─── Print / export currently displayed (filtered) activities ──────────
  const handlePrint = async () => {
    if (!printRef.current || printing) return;
    try {
      setPrinting(true);
      const filenameDateBit =
        startDate || endDate
          ? `_${startDate || "start"}_to_${endDate || "end"}`
          : "";
      await downloadPDF(
        printRef.current,
        `activity_report${filenameDateBit}.pdf`
      );
    } catch (err) {
      console.error("PDF export error:", err);
      setError(t("studentDetails.activityFeed.printError"));
    } finally {
      setPrinting(false);
    }
  };

  const getIconByType = (type) => {
    const icons = {
      quran: <QuranIcon style={{ width: 32, height: 32 }} />,
      meds: <MedsIcon style={{ width: 32, height: 32 }} />,
      checkin: <CheckInIcon style={{ width: 32, height: 32 }} />,
      photo: <MediaIcon style={{ width: 32, height: 32 }} />,
      observation: <ObservationIcon style={{ width: 32, height: 32 }} />,
      incident: <IncidentIcon style={{ width: 32, height: 32 }} />,
      note: <NotesIcon style={{ width: 32, height: 32 }} />,
      kudos: <KudosIcon style={{ width: 32, height: 32 }} />,
      quranassessment: <AssessmentIcon style={{ width: 32, height: 32 }} />,
      recitation: <RecitationIcon style={{ width: 32, height: 32 }} />,
      custom: <CustomIcon style={{ width: 32, height: 32 }} />,
    };
    return icons[type] || <CustomIcon style={{ width: 32, height: 32 }} />;
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
          justifySelf: "center",
          alignItems: "flex-start",
        }}
      >
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>{t("studentDetails.activityFeed.activity")}</InputLabel>
          <Select
            value={activityType}
            label={t("studentDetails.activityFeed.activity")}
            onChange={(e) => setActivityType(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                height: "0.5rem",
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
          >
            {activityOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {t(label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          type="date"
          size="small"
          label={t("studentDetails.activityFeed.fromDate")}
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(dateError)}
          sx={{ minWidth: 150 }}
        />

        <TextField
          type="date"
          size="small"
          label={t("studentDetails.activityFeed.toDate")}
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(dateError)}
          sx={{ minWidth: 150 }}
        />

        <Button
          variant="outlined"
          startIcon={printing ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={printing || loading || activities?.length === 0}
          sx={{ alignSelf: "center", height: 40 }}
        >
          {t("studentDetails.activityFeed.print")}
        </Button>
      </Box>

      {dateError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {dateError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : activities?.length === 0 ? (
        <Alert severity="info">
          {t("studentDetails.activityFeed.noActivities")}
        </Alert>
      ) : (
        <>
          {/* Only this ref'd container is exported to PDF */}
          <Box ref={printRef}>
            {activities?.map((activity, index) => {
              const { type, datetime, visible_staff_only, details } = activity;
              return (
                <Card key={index} variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
                  <CardContent sx={{ display: "flex", gap: 2 }}>
                    <Box>{getIconByType(type)}</Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, textTransform: "capitalize" }}
                      >
                        {type === "custom"
                          ? details.title
                          : t(`studentDetails.activityFeed.${type?.toLowerCase()}`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatDate(datetime)}
                      </Typography>
                      <ActivityCard type={type} details={details} />
                      {visible_staff_only && (
                        <Typography
                          variant="body2"
                          fontStyle="italic"
                          fontWeight="bold"
                          color="text.secondary"
                          className="mt-3"
                        >
                          {t("studentDetails.activityFeed.visibleStaffOnly")}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {loadingMore && (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {hasMore && <Box ref={observerRef} sx={{ height: 1 }} />}
        </>
      )}
    </Box>
  );
};

export default ActivityFeed;